export enum REGISTRATION_MODE {
    NORMAL_MODE = 0,
    INSTALLATION_MODE = 1,
}
export interface LabelOption {
    key: TAB_TYPE;
    label: TAB_TYPE;
    disable: boolean;
    tooltip: string;
}

export interface PaginationModel {
    page: number;
    pageSize: number;
}

export interface SortModel {
    field: string | null;
    sort: 'asc' | 'desc' | null;
}

export interface ApLockInfo {
    isLock: boolean;
    mac: string;
}

export type booleanGroup = {
    [key: string]: boolean;
};

export interface InfraDevice {
    mac: string;
    device_type_id: number; // device_type_id: 1: RT, 2: AP, 3:Tag
    confirmed: number; // 0: unconfirmed, 1:confirmed, 2:in progress
    rssi: number;
    gw_mac: string;
    parent_mac: string;
    ed_status: 'ONLINE' | 'OFFLINE';
    ed_status_time: string | null;
    network_mode: string | null;
    wakeup_mode: string | null;
    current_sub_channel: number | null;
    current_treeid: number | null;
    userset_treeid: number | null;
    userset_sub_channel: number | number;
    switch_channel_mode: string | null;
    already_in_temp_devices: number | null;
    store_id: number | null;
}

export interface HandleFetchInfraDevicesResponse {
    aaData: InfraDevice[];
    draw: number;
    display_request: number;
    total_records: number;
    max_page: number;
    explanation: string;
}
export interface DialogState {
    isApOpen: boolean;
    isStoreStateOpen: boolean;
    isTagOpen: boolean;
    isApLockOpen: boolean;
    isDeleteOpen: boolean;
}
export type DialogKey = keyof DialogState;
export interface LoadingState {
    storeStateLoading: boolean;
    deviceTableLoading: boolean;
}
export interface ButtonState {
    delete: {
        status: boolean;
        tooltip: string;
        warning: string;
    };
    addAP: boolean;
    addTag: boolean;
    refresh: boolean;
    editAP: boolean;
    confirmRT: boolean;
}
type DeviceInfoStatus = 'init' | 'idle' | 'success' | 'loading';

interface DeviceInfoBase {
    status: DeviceInfoStatus;
    isLoading: boolean;
    sortModel: SortModel;
    paginationModel: PaginationModel;
    infraDeviceInfo: InfraDevice[];
    selectedInfraDevices: InfraDeviceRow[];
    rowCount: number;
    doingTask: boolean;
}

export type DeviceInfoState = DeviceInfoBase;

export type DeviceInfoStatePayload = Partial<DeviceInfoBase>;
export interface TagStatus {
    confirmed: number;
    total: number;
}
export interface TagState {
    isOnProgress: boolean;
    status: TagStatus;
}
export interface RegistrationState {
    labelOptions: LabelOption[];
    editAPInfo: InfraDevice | null;
    storeDeviceTotal: number;
    registrationMode: REGISTRATION_MODE;
    infraDevice: InfraDevice[];
    searchMac: string;
    toggleTagProgress: boolean;
    apLockInfo: ApLockInfo;
    dialogState: DialogState;
    loadingState: LoadingState;
    currentTab: TAB_TYPE;
    deviceInfoState: DeviceInfoState;
    tagStatus: TagStatus;
    tagState: TagState;
}

export type RegistrationAction =
    | { type: 'TOGGLE_LABEL_DISABLE'; payload: { label: TAB_TYPE[] } }
    | { type: 'SET_AP_INFO'; payload: InfraDevice }
    | { type: 'SET_REGISTRATION_MODE'; payload: REGISTRATION_MODE }
    | { type: 'SET_STORE_STATUS'; payload: REGISTRATION_MODE }
    | { type: 'SET_STORE_DEVICE_TOTAL'; payload: number }
    | { type: 'SET_INFRA_DEVICES'; payload: InfraDevice[] }
    | { type: 'SET_SEARCH_MAC'; payload: string }
    | { type: 'SET_AP_LOCK_INFO'; payload: ApLockInfo }
    | { type: 'CLEAR' }
    | { type: 'SET_INIT_TABLE_STATE' }
    | { type: 'SET_TAB'; payload: TAB_TYPE }
    | { type: 'SET_TABLE_STATE'; payload: DeviceInfoStatePayload }
    | { type: 'SET_TAG_STATUS'; payload: TagStatus }
    | { type: 'SET_TAG'; payload: Partial<TagState> }
    | { type: 'SET_SUPPORT_ASSET_LIST'; payload: boolean }
    | {
          type: 'TOGGLE_DIALOG';
          payload: {
              dialog: keyof DialogState;
              isOpen: boolean;
          };
      }
    | {
          type: 'TOGGLE_LOADING';
          payload: {
              key: keyof LoadingState;
              isLoading: boolean;
          };
      };

// AP dialog
export interface ValidatedMessage {
    mac: string;
    channel: string;
    treeId: string;
}
export enum INPUT_CATEGORY {
    AP_MAC = 'AP_MAC',
    SUB_CHANNEL = 'SUB_CHANNEL',
    TREE_ID = 'TREE_ID',
}
export interface RegisterAPAndRTRequest {
    type: 'ap' | 'rt';
    mac: string;
    channel?: number | null;
    treeid?: number | null;
    store_id: number;
}
export interface APDialogState {
    mac: string;
    channel: number | null;
    treeId: number | null;
    validatedMessage: ValidatedMessage;
}

export type APDialogAction =
    | { type: 'SET_MAC'; payload: string }
    | { type: 'SET_CHANNEL'; payload: number | null }
    | { type: 'SET_TREE_ID'; payload: number | null }
    | { type: 'SET_VALIDATED_MESSAGE'; payload: ValidatedMessage }
    | { type: 'SET_EDIT_DATA'; payload: InfraDevice }
    | { type: 'CLEAR_DIALOG_DATA' };

// Tab
export enum TAB_TYPE {
    AP = 'AP',
    RT = 'RT',
    TAG = 'TAG',
    NONE = 'NONE',
}

export type TabType = keyof typeof TAB_TYPE;

export enum DEVICE_TYPE {
    RT = 1,
    AP = 2,
    TAG = 3,
}
export enum DEVICE_STATUS {
    UN_CONFIRMED = 0,
    CONFIRMED = 1,
    IN_PROGRESS = 2,
    IN_PROGRESS_PENDING = 3,
}

// Delete Dialog
export interface DeleteTagsResponse {
    temp_tags_already_confirmed: string[];
    temp_tags_removed: string[];
    temp_tags_invalid: string[];
}

export interface DeleteAPAndRTResponse {
    invalid_macs: string[];
    unprocessed_macs: string[];
    removed_macs: string[];
}

export interface DeleteReportData extends Partial<DeleteTagsResponse>, Partial<DeleteAPAndRTResponse> {
    validateMessage: string | undefined;
}

export type InfraDeviceRow = InfraDevice & {
    isAP: boolean;
    isRT: boolean;
    isOnline: boolean;
    isLocked: boolean;
    edStatusDisplay: string; // e.g. "ONLINE 【 LOCKED 】"
    edStatusColor: string; // from scss variables
    isRTUnconfirmed: boolean; // RT 且 confirmed === UN_CONFIRMED
    edStatusLocalTime: string | null;
};
